import { ChangeEvent, Fragment } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import classNames from 'classnames/bind'

import { Table, Tr, Th, Td, useHoverByClass } from 'app/common/ui-components/table'

import Paging from 'app/common/ui/Paging'
import type { SortHandle, SortState } from 'app/common/containers/withSortState'
import CSSSticky from 'app/common/ui-components/css-sticky'
import { ReservationHistoryModal } from 'app/common/components-next/reservation-history'
import { FundReturnModal } from 'app/common/components-next/fund-return'
import { useCurrencySign, options as co } from 'app/common/components-next/currency'
import DetailsHeader from 'app/components/common/reports/DetailsHeader'
import { isValueZero } from 'app/common/templates-next/table-template'
import { Name } from 'app/common/classes/name'

import enhanceUsers from './enhanceUsers'
import type { UserType } from './selectActual'

import styles from './Users.scss'
import PlainTooltip from 'app/common/ui-next/plain-tooltip'

const cx = classNames.bind(styles)
const sameRowBase = 'row_'

type Props = {
  handlePage: (e: ChangeEvent<HTMLElement>) => void
  users: Array<UserType>
  currentPage: number
  total: number
  rows: number
  sort: SortState
  handleSort: SortHandle
  onInvalidateReport: () => void
  isPayerDataVisible?: boolean
}

const Users = ({
  handlePage,
  users,
  currentPage,
  total,
  rows,
  sort: { sortDirection, sortKey },
  handleSort,
  onInvalidateReport,
  isPayerDataVisible,
}: Props) => {
  const current: boolean = useSelector(
    (state: any) => state.reports.balance.params.current,
    shallowEqual,
  )
  const { e_money_enabled: eMoneyEnabled } = useSelector((state: any) => state.dealer, shallowEqual)
  const currency = useCurrencySign(co.COMMA_SPACE_BEFORE)

  const [onMouseEnter, onMouseLeave] = useHoverByClass(sameRowBase)

  return (
    <Fragment>
      <DetailsHeader reportTitle="Остаток средств" />

      {/* ЗАГОЛОВОК */}
      <CSSSticky
        number={2}
        className={styles.stickableTableHeader}
        stickyClassName={styles.tableSticked}
        shadowClassName={styles.shadow}
      >
        <Table className={styles.table}>
          <thead>
            <Tr header>
              {!isPayerDataVisible && (
                <Th
                  id="empty1"
                  className={cx(styles.headerCell, {
                    cellEmpty: !current,
                    cellEmptySmall: current,
                  })}
                >
                  &nbsp;
                </Th>
              )}

              <Th
                id="header1"
                className={cx(styles.headerCell, styles.cellName)}
                sortKey="name"
                sortDirection={sortKey === 'name' ? sortDirection : undefined}
                onSortArrowClick={handleSort}
                alignLeft
                {...(isPayerDataVisible && { rowSpan: 2 })}
              >
                {'ФИО'}
              </Th>

              <Th
                id="header2"
                className={cx(styles.headerCell, styles.cellGroup)}
                sortKey="group"
                sortDirection={sortKey === 'group' ? sortDirection : undefined}
                onSortArrowClick={handleSort}
                alignLeft
                {...(isPayerDataVisible && { rowSpan: 2 })}
              >
                {'Класс / Должность'}
              </Th>

              <Th
                id="header3"
                className={cx(styles.headerCell, styles.cellBalance)}
                sortKey="balance"
                sortDirection={sortKey === 'balance' ? sortDirection : undefined}
                onSortArrowClick={handleSort}
                alignRight
                {...(isPayerDataVisible && { rowSpan: 2 })}
              >
                {`Баланс${currency}`}
              </Th>

              {isPayerDataVisible && (
                <Th
                  id="header5"
                  className={cx(styles.headerCell, styles.cellParents)}
                  colSpan={3}
                  alignCenter
                >
                  {'Родители'}
                </Th>
              )}

              {current && (
                <Th
                  id="header4"
                  className={cx(styles.headerCell, styles.cellLinks)}
                  alignLeft
                  {...(isPayerDataVisible && { rowSpan: 2 })}
                >
                  {'Дополнительно'}
                </Th>
              )}

              {!isPayerDataVisible && (
                <Th
                  id="empty2"
                  className={cx(styles.headerCell, {
                    cellEmpty: !current,
                    cellEmptySmall: current,
                  })}
                >
                  &nbsp;
                </Th>
              )}
            </Tr>
            {isPayerDataVisible && (
              <Tr>
                <Th id="parent_fio" className={styles.cellParentsCol} alignCenter ignoreFirstChild>
                  {'ФИО'}
                </Th>
                <Th id="parent_phone" className={styles.cellParentsCol} alignCenter>
                  {'Телефон'}
                </Th>
                <Th id="parent_email" className={styles.cellParentsCol} alignCenter ignoreLastChild>
                  {'E-mail'}
                </Th>
              </Tr>
            )}
          </thead>
        </Table>
      </CSSSticky>

      <Table className={styles.table}>
        <tbody>
          {users.map(({ id, name, group, balance, customerId, parents }, rowIndex) => {
            const rowSpan = isPayerDataVisible && parents && parents.length > 1 ? parents.length : 1
            const rowSpanProps = rowSpan > 1 ? { rowSpan } : {}
            return (
              <Fragment key={id}>
                <Tr
                  className={`${sameRowBase}${rowIndex}`}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  interlineBorder
                  alignTop
                >
                  {!isPayerDataVisible && (
                    <Td
                      headers="empty1"
                      className={cx({ cellEmpty: !current, cellEmptySmall: current })}
                    >
                      &nbsp;
                    </Td>
                  )}

                  <Td headers="header1" className={styles.cellName} {...rowSpanProps}>
                    {name}
                  </Td>

                  <Td headers="header2" className={styles.cellGroup} {...rowSpanProps}>
                    {group}
                  </Td>

                  <Td
                    headers="header3"
                    className={cx(styles.cellBalance, { zero: isValueZero(balance) })}
                    alignRight
                    {...rowSpanProps}
                  >
                    {balance}
                  </Td>

                  {isPayerDataVisible && (
                    <>
                      <Td headers="parent_fio" className={styles.cellParentsCol} alignLeft>
                        <PlainTooltip
                          text={parents?.[0]?.name && new Name(parents[0].name).toString()}
                        >
                          {parents?.[0]?.name && new Name(parents[0].name).toString()}
                        </PlainTooltip>
                      </Td>
                      <Td headers="parent_phone" className={styles.cellParentsCol} alignCenter>
                        <PlainTooltip text={parents?.[0]?.phone || '—'}>
                          {parents?.[0]?.phone || '—'}
                        </PlainTooltip>
                      </Td>
                      <Td headers="parent_email" className={styles.cellParentsCol} alignCenter>
                        <PlainTooltip text={parents?.[0]?.email || '—'}>
                          {parents?.[0]?.email || '—'}
                        </PlainTooltip>
                      </Td>
                    </>
                  )}

                  {current && (
                    <Td headers="header4" className={styles.cellLinks} {...rowSpanProps}>
                      <ReservationHistoryModal customerId={customerId} name={name} group={group}>
                        {'Резерв-ние'}
                      </ReservationHistoryModal>{' '}
                      {parseFloat(balance) > 0 && !eMoneyEnabled && (
                        <FundReturnModal
                          customerId={customerId}
                          name={name}
                          group={group}
                          onRefund={onInvalidateReport}
                        >
                          {'Возврат'}
                        </FundReturnModal>
                      )}
                    </Td>
                  )}

                  {!isPayerDataVisible && (
                    <Td
                      headers="empty2"
                      className={cx({ cellEmpty: !current, cellEmptySmall: current })}
                    >
                      &nbsp;
                    </Td>
                  )}
                </Tr>

                {parents &&
                  rowSpan > 1 &&
                  parents.slice(1).map((parent, index) => {
                    const parentName = parent.name && new Name(parent.name).toString()
                    return (
                      <Tr
                        key={parentName || index}
                        className={`${sameRowBase}${rowIndex}`}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        alignTop
                      >
                        <Td
                          headers="parent_fio"
                          className={styles.cellParentsCol}
                          alignLeft
                          ignoreFirstChild
                        >
                          <PlainTooltip text={parentName}>{parentName}</PlainTooltip>
                        </Td>
                        <Td headers="parent_phone" className={styles.cellParentsCol} alignCenter>
                          <PlainTooltip text={parent.phone}>{parent.phone || '—'}</PlainTooltip>
                        </Td>
                        <Td
                          headers="parent_email"
                          className={styles.cellParentsCol}
                          alignCenter
                          ignoreLastChild
                        >
                          <PlainTooltip text={parent.email}>{parent.email || '—'}</PlainTooltip>
                        </Td>
                      </Tr>
                    )
                  })}
              </Fragment>
            )
          })}
        </tbody>
      </Table>

      {total > rows && (
        <Paging
          className={styles.pager}
          total={total}
          pageSize={rows}
          currentPage={currentPage}
          currentPageSize={users.length}
          onPage={handlePage}
        />
      )}
    </Fragment>
  )
}

export default enhanceUsers(Users)
